import { useTasks } from "@/api/workflow-hooks";
import { Badge } from "@/components/badge";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/card";
import { Input } from "@/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import { cn } from "@/lib/utils";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import type { GetTasksResponseItem } from "@repo/api";
import { RunStatus } from "@repo/shared/workflow";
import { format } from "date-fns";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateTaskDialog } from "./create-task-dialog";

type ClientType = "AFICS" | "American Integrity" | "Other";

const TASK_FILTER_ALL = "ALL" as const;

export type Task = {
  id: number;
  reportNumber: string;
  clientName: ClientType;
  status: RunStatus;
  createdAt?: string;
};

interface TaskRowProps {
  task: GetTasksResponseItem;
}

const TaskRow = ({ task }: TaskRowProps) => {
  const navigate = useNavigate();

  const handleViewTask = () => {
    // navigate(`/tasks/${task.clientName}/${task.reportNumber}`);
    navigate(`/tasks/${task.id}`);
  };

  const status = task.latestRun?.status ?? RunStatus.PENDING;

  return (
    <TableRow
      onClick={handleViewTask}
      className="hover:bg-accent hover:text-accent-foreground hover:cursor-pointer"
    >
      <TableCell className="text-lg font-medium">{task.name}</TableCell>
      <TableCell>
        <Badge
          className={cn("text-base border-none", {
            "bg-secondary text-secondary-foreground":
              status === RunStatus.PENDING ||
              status === RunStatus.PROCESSING ||
              status === RunStatus.REVIEW,
            "bg-orange-100 text-secondary-foreground":
              status === RunStatus.REVIEW,
            "bg-emerald-100 text-secondary-foreground":
              status === RunStatus.COMPLETED,
            "bg-gray-200 text-secondary-foreground":
              status === RunStatus.FAILED,
          })}
          variant={status === RunStatus.PENDING ? "secondary" : "outline"}
        >
          {_.startCase(status?.toLocaleLowerCase() ?? "unknown")}
        </Badge>
      </TableCell>
      <TableCell className="text-lg">{task.workflow.name}</TableCell>
      <TableCell className="text-lg text-right">
        {task.createdAt
          ? format(new Date(task.createdAt), "MM/dd/yyyy hh:mm a")
          : ""}
      </TableCell>
    </TableRow>
  );
};

interface TaskSectionProps {
  title: string;
  tasks: GetTasksResponseItem[];
  statusFilter: RunStatus | typeof TASK_FILTER_ALL;
  setStatusFilter: (filter: RunStatus | typeof TASK_FILTER_ALL) => void;
}

const TaskSection = ({
  title,
  tasks,
  statusFilter,
  setStatusFilter,
}: TaskSectionProps) => {
  const [searchFilter, setSearchFilter] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Task;
    direction: "asc" | "desc";
  } | null>(null);

  const handleSort = (key: keyof Task) => {
    let direction: "asc" | "desc" = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedTasks = [...tasks]
    .filter(
      (task) =>
        statusFilter === TASK_FILTER_ALL ||
        task.latestRun?.status === statusFilter,
    )
    .filter((task) =>
      task.name.toLowerCase().includes(searchFilter.toLowerCase()),
    );

  if (sortConfig !== null) {
    sortedTasks.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (sortConfig.key === "reportNumber") {
        // Convert report numbers to actual numbers for comparison
        const aNum = Number.parseInt(aVal as string);
        const bNum = Number.parseInt(bVal as string);
        return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
      }

      if (sortConfig.key === "createdAt") {
        // Convert dates to timestamps for comparison
        const aDate = aVal ? new Date(aVal as string).getTime() : 0;
        const bDate = bVal ? new Date(bVal as string).getTime() : 0;
        return sortConfig.direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      // Default string comparison for other fields
      if (aVal < bVal) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }

  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center space-x-4">
          <CardTitle className="text-lg font-medium">{title}</CardTitle>
          <Select
            value={statusFilter}
            onValueChange={setStatusFilter}
            defaultValue="All"
          >
            <SelectTrigger className="w-[130px] h-[28px] text-sm">
              <SelectValue>
                {_.startCase(statusFilter.toLocaleLowerCase()) || "All"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TASK_FILTER_ALL}>All</SelectItem>
              <SelectItem value={RunStatus.PROCESSING}>Processing</SelectItem>
              <SelectItem value={RunStatus.REVIEW}>Review</SelectItem>
              <SelectItem value={RunStatus.COMPLETED}>Complete</SelectItem>
              <SelectItem value={RunStatus.FAILED}>Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              id="searchFilter"
              type="text"
              placeholder="Search tasks..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="w-[180px] pl-8"
            />
          </div>
          <CreateTaskDialog />
          {/* <Button variant="default" size="lg" className="h-[34px] w-[100px]" onClick={() => setIsDialogOpen(true)}>
            <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">New Task</span>
          </Button> */}
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="text-lg cursor-pointer"
                onClick={() => handleSort("reportNumber")}
              >
                Task ID{" "}
                {sortConfig?.key === "reportNumber" &&
                  (sortConfig.direction === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead
                className="text-lg cursor-pointer"
                onClick={() => handleSort("status")}
              >
                Status{" "}
                {sortConfig?.key === "status" &&
                  (sortConfig.direction === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead
                className="text-lg cursor-pointer"
                onClick={() => handleSort("clientName")}
              >
                Workflow{" "}
                {sortConfig?.key === "clientName" &&
                  (sortConfig.direction === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead
                className="text-lg text-right cursor-pointer"
                onClick={() => handleSort("createdAt")}
              >
                Created At{" "}
                {sortConfig?.key === "createdAt" &&
                  (sortConfig.direction === "asc" ? "↑" : "↓")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedTasks.map((task) => (
              <TaskRow key={task.id} task={task} />
            ))}
          </TableBody>
        </Table>
      </CardContent>
      <CardFooter>
        <div className="text-base text-muted-foreground">
          Showing <strong>{sortedTasks.length}</strong> of{" "}
          <strong>{tasks.length}</strong> tasks
        </div>
      </CardFooter>
    </Card>
  );
};

const Tasks: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<
    RunStatus | typeof TASK_FILTER_ALL
  >(TASK_FILTER_ALL);

  const { data: tasks } = useTasks();

  return (
    <div className="flex flex-col w-full bg-background">
      <main className="flex flex-1 flex-col gap-4 mt-2 px-8">
        {tasks && (
          <TaskSection
            title="Tasks"
            tasks={tasks.items}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
          />
        )}
      </main>
    </div>
  );
};

export default Tasks;
